import React, { useEffect, useRef, useState } from 'react'
import { Button, Table } from 'react-bootstrap'
import { FaDownload, FaArrowRotateRight, FaArrowsRotate } from 'react-icons/fa6'
import { FaFile, FaLink, FaEdit } from 'react-icons/fa'
import FeedInputModal from './feed-input-modal'
import { IFeed, IFeed_Input, IFeed_Input_UpdateInterval } from './types'
import { formatNumberLocale, getUserTimezoneOffset } from '../../../_utils'
import useFetch from '../../../_fetch'
import DownloadModal from './download-modal'
import * as DateFns from 'date-fns'
import { useAuth } from '../../../auth/auth-context'
import ConfirmRemoveModal from './confirm-remove-modal'  // Import the new modal

const isDev = process.env.NODE_ENV === 'development'

const formatUpdateInterval = (interval: IFeed_Input_UpdateInterval): string => {
    if(interval.frequency === 'hourly' || !('timezoneOffset' in interval)) return 'Hourly'

    const { frequency, hour, dayOfWeek, dayOfMonth } = interval
    const formattedTime = `${hour}:00`

    switch (frequency) {
        case 'daily':
            return `Everyday ${formattedTime}`
        case 'weekly':
            return `Every ${dayOfWeek} ${formattedTime}`
        case 'monthly':
            return `Every ${dayOfMonth} ${formattedTime}`
        default:
            return `Unknown Frequency`
    }
}

const formatGMT = (interval: IFeed_Input_UpdateInterval): string => {
    if(interval.frequency === 'hourly' || !('timezoneOffset' in interval)) return ''
    const { timezoneOffset } = interval
    return `GMT ${timezoneOffset >= 0 ? '+' : ''}${timezoneOffset}`
}

const Feed = () => {
    const { doFetch } = useFetch()
    const { user } = useAuth()

    const [showModal, setShowModal] = useState(false)
    const [feeds, setFeeds] = useState<IFeed[]>([])
    const [runningFeeds, setRunningFeeds] = useState<{ [id: string]: boolean }>({})
    const [currentFeed, setCurrentFeed] = useState<IFeed | undefined>(undefined)

    const [downloadModalVisible, setDownloadModalVisible] = useState(false)
    const [downloadInfo, setDownloadInfo] = useState({ title: '', link: '' })

    const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false)  // State for showing the confirm modal
    const [feedToRemove, setFeedToRemove] = useState<IFeed | null>(null)  // Store the feed to be removed

    useEffect(() => {
        (async () => {
            const res = await doFetch('/feed/get-feeds')
            if (res.status === 200) {
                const data = await res.json()
                setFeeds(data.feeds)
                setRunningFeeds(data.runningFeeds)
                refreshFeeds(data.runningFeeds)
            }
        })()
    }, [])

    const handleOpenModal = (feed: IFeed) => {
        setCurrentFeed(feed)
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentFeed(undefined)
    }

    const handleSaveFeedInput = async (input: IFeed_Input) => {
        const url = currentFeed?.id ? '/feed/update-feed' : '/feed/create-feed'
        
        const formData = new FormData()
        const fileLinkIds: string[] = []
        input.links.forEach(l => {
            if (typeof l.file !== 'string' && l.file) {
                formData.append('files', l.file)
                l.file = l.file.name
                fileLinkIds.push(l.id)
            }
        })

        formData.append('id', currentFeed?.id || '')
        formData.append('input', JSON.stringify(input))
        formData.append('fileLinkIds', JSON.stringify(fileLinkIds))

        const res = await doFetch(url, formData)

        if (res.status === 200) {
            const newFeeds = [...feeds]
            const data = await res.json()
            if (currentFeed?.id) {
                newFeeds[newFeeds.findIndex(feed => feed.id === currentFeed.id)].input = data.input
            } else {
                newFeeds.push({ ...data, output: [] })
            }
            setFeeds(newFeeds)
        }
        handleCloseModal()
    }

    const isRefreshing = useRef(false)

    const refreshFeeds: any = async (currFeeds: { [id: string]: boolean }) => {
        if (isRefreshing.current || Object.keys(currFeeds).length === 0) return

        isRefreshing.current = true

        await new Promise((r) => setTimeout(r, 5000))

        const res = await doFetch('/feed/get-running-feeds')
        const newRunningFeeds = await res.json()

        setRunningFeeds(newRunningFeeds)

        if (Object.keys(newRunningFeeds).length === 0) {
            const res = await doFetch('/feed/get-feeds')
            if (res.status === 200) {
                const data = await res.json()
                setFeeds(data.feeds)
                setRunningFeeds(data.runningFeeds)
                isRefreshing.current = false
                return await refreshFeeds(data.runningFeeds)
            }
        }

        isRefreshing.current = false

        await refreshFeeds(newRunningFeeds)
    }

    const updateFeed = async (feedId: string, linkId: string) => {
        const res = await doFetch('/feed/run-feed', { feedId, linkId })
        if (res.status >= 400) return

        const currRunningFeeds = await res.json()

        setRunningFeeds(currRunningFeeds)

        refreshFeeds(currRunningFeeds)
    }

    const handleDownloadClick = (link: string, title: string) => {
        setDownloadInfo({ title, link })
        setDownloadModalVisible(true)
    }

    const handleDownloadModalClose = () => {
        setDownloadModalVisible(false)
        setDownloadInfo({ title: '', link: '' })
    }

    const handleRemoveFeed = async () => {
        if (!feedToRemove) return
        const res = await doFetch(`/feed/remove-feed/`, { feedId: feedToRemove.id })
        console.log('feedToRemove', feedToRemove)
        if (res.status === 200) {
            setFeeds(feeds.filter(f => f.id !== feedToRemove.id))
        }
        setShowConfirmRemoveModal(false)
        setFeedToRemove(null)
        handleCloseModal()
    }

    return (
        <div>
            <Button
                variant="primary"
                className="mt-2"
                onClick={() => handleOpenModal({
                    id: '',
                    input: {
                        name: '',
                        rules: [],
                        updateInterval: { frequency: 'daily', hour: '00', timezoneOffset: getUserTimezoneOffset() },
                        links: []
                    },
                    output: []
                })}
            >
                Create feed
            </Button>

            <FeedInputModal
                show={showModal}
                onClose={handleCloseModal}
                onSave={handleSaveFeedInput}
                feedInput={currentFeed?.input}
                showRemove={!!currentFeed?.id}
                onRemove={() => {
                    setFeedToRemove(currentFeed!)  // Set the feed to be removed
                    setShowConfirmRemoveModal(true)  // Show the confirm modal
                }}
            />

            <ConfirmRemoveModal
                show={showConfirmRemoveModal}
                onHide={() => setShowConfirmRemoveModal(false)}
                onConfirm={handleRemoveFeed}
                feedName={feedToRemove?.input.name || ''}
            />

            <DownloadModal
                show={downloadModalVisible}
                onHide={handleDownloadModalClose}
                downloadInfo={downloadInfo}
            />

            <Table className="mt-4" size='sm' responsive>
                <thead>
                    <tr>
                        <th>Feed</th>
                        <th>Update Interval</th>
                        <th>Status</th>
                        <th>Products Before / After</th>
                        <th>Last Updated</th>
                        <th>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {feeds.map((feed, ind) => (
                        <React.Fragment key={feed.id + '-' + ind}>
                            <tr key={feed.id}>
                                <th className='border-0 border-top'>
                                    <a className='me-1' href="#" onClick={(e) => {
                                        e.preventDefault()
                                        handleOpenModal(feed)
                                    }}>
                                        <FaEdit />
                                    </a>
                                    {feed.input.name}
                                </th>
                                <td className='border-0 border-top'>
                                    {formatUpdateInterval(feed.input.updateInterval)}
                                    <i className='ms-1 fw-light'>
                                        <small>{formatGMT(feed.input.updateInterval)}</small>
                                    </i>
                                </td>
                                <td className='border-0 border-top' colSpan={6}></td>
                            </tr>

                            {feed.input.links.map((link, index) => {
                                const output = feed.output.find(output => output.linkId === link.id)
                                const isUpdating = runningFeeds[feed.id + '_' + link.id] || false
                                return (
                                    <tr key={`${feed.id}-output-${index}`}>
                                        <td colSpan={2} className='border-0'>
                                            <div className="d-flex align-items-center">
                                                <div>
                                                    {link.dataSourceType === 'file' ? (
                                                        <FaFile className="me-2" style={{ fontSize: '0.7rem', color: '#6c757d' }} />
                                                    ) : (
                                                        <FaLink className="me-2" style={{ fontSize: '0.7rem', color: '#6c757d' }} />
                                                    )}
                                                    {link.name}
                                                </div>
                                                <div className="ms-auto me-1" style={{ color: 'rgb(13 110 253)' }}>
                                                    <FaDownload
                                                        className={`me-1${output?.status === 1 ? ' cursor-pointer' : ''}`}
                                                        style={output?.status === 1 ? { fontSize: '1rem' } : { opacity: 0.5 }}
                                                        title={output?.status === 1 ? 'Download' : 'Please update the feed to download'}
                                                        onClick={() => {
                                                            if (output?.status !== 1) return
                                                            const origin = isDev ? 'http://localhost:3050' : 'https://api.affdash.nordpar.com'
                                                            const url = origin + '/feed/' + user?.username + '/' + feed.id + '/' + link.id
                                                            handleDownloadClick(url, link.name)
                                                        }}
                                                    />
                                                    {isUpdating ?
                                                        <FaArrowsRotate
                                                            style={{ fontSize: '1rem', opacity: 0.5 }}
                                                            title='Updating...'
                                                        /> :
                                                        <FaArrowRotateRight
                                                            className='cursor-pointer'
                                                            style={{ fontSize: '1rem' }}
                                                            title={"Update"}
                                                            onClick={() => {
                                                                setRunningFeeds({ ...runningFeeds, [feed.id + '_' + link.id]: true })
                                                                updateFeed(feed.id, link.id)
                                                            }}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                        <td className={(output && (output?.status === 1 ? 'bg-success' : output?.status === 2 ? 'bg-warning' : 'bg-danger')) + ' border-0'}>
                                            {isUpdating ? 'Updating...' : !output ? 'Pending' : output?.status === 1 ? 'Ok' : output?.status === 2 ? 'Warning' : 'Error'}
                                        </td>
                                        <td className='border-0'>
                                            {output?.productsBefore !== undefined ? (
                                                formatNumberLocale(output.productsBefore, 0) + ' / ' + formatNumberLocale(output?.productsAfter, 0)
                                            ) : ''}
                                        </td>
                                        <td className='border-0'>
                                            {output?.lastUpdated ? DateFns.format(new Date(output?.lastUpdated), 'yyyy-MM-dd HH:mm:ss') : ''}
                                        </td>
                                        <td className='border-0' style={{ width: '300px' }}>
                                            {output?.message}
                                        </td>
                                    </tr>
                                )
                            })}
                        </React.Fragment>
                    ))}
                </tbody>
            </Table>
        </div>
    )
}

export default Feed
